/**
 * Resolve the active local Claude Code bridge URL for OCR / grading.
 *
 * The bridge shells out to the boss's subscription Claude so OCR/grading works
 * with zero API key. On localhost it lives at a fixed port; on the deployed
 * build (pages.dev / mobile) the bridge registers a dynamic public URL with the
 * worker, which exposes it via `GET {WORKER_URL}/bridge/url → { url:string|null }`.
 *
 * Resolution order (mirrors the legacy synchronous `bridgeUrl()` it replaces,
 * plus a worker lookup for the deployed case):
 * - VITE_LOCAL_CLAUDE_BRIDGE set → use it (manual override / custom host).
 * - else on localhost (dev) → auto-default to http://localhost:8910.
 * - else (prod / non-localhost) → ask the worker for the registered URL.
 * - any error / no URL → undefined; callers then fall back to the worker AI.
 */

/** Default local bridge port (tools/claude-bridge/server.mjs). */
const DEFAULT_BRIDGE_URL = 'http://localhost:8910';
/** How long to wait for the worker `/bridge/url` lookup before giving up. */
const LOOKUP_TIMEOUT_MS = 2000;
/** Session cache TTL: avoid hitting /bridge/url on every OCR/grade call. */
const CACHE_TTL_MS = 60_000;

/** Read lazily so tests (and Vite) can inject env at call time. */
function workerUrl(): string | undefined {
  const v = import.meta.env.VITE_WORKER_URL as string | undefined;
  return v ? v : undefined;
}

/** Read lazily so tests (and Vite) can inject env at call time. */
function envOverride(): string | undefined {
  const v = import.meta.env.VITE_LOCAL_CLAUDE_BRIDGE as string | undefined;
  return v ? v : undefined;
}

function isLocalhost(): boolean {
  return (
    typeof location !== 'undefined' &&
    (location.hostname === 'localhost' || location.hostname === '127.0.0.1')
  );
}

/** Module-level session cache for the worker lookup result. */
let cached: { url: string | undefined; at: number } | null = null;

/** Ask the worker for the dynamically-registered bridge URL (deployed build). */
async function fetchFromWorker(): Promise<string | undefined> {
  const url = workerUrl();
  if (!url) return undefined;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${url}/bridge/url`, { signal: controller.signal });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { url: string | null };
    return body.url ?? undefined;
  } catch {
    // Lookup unreachable / timed out / bad JSON → no bridge.
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve the bridge URL for the current environment. Returns `undefined` when
 * no bridge is available so callers fall through to the worker / browser path.
 *
 * The deployed-build worker lookup is cached for {@link CACHE_TTL_MS} so repeat
 * OCR/grade calls in one session don't re-hit `/bridge/url` each time.
 */
export async function resolveBridgeUrl(): Promise<string | undefined> {
  // Manual override and localhost auto-detect are cheap + synchronous: never cache.
  const override = envOverride();
  if (override) return override;
  if (isLocalhost()) return DEFAULT_BRIDGE_URL;

  // Deployed build: ask the worker, with a short session cache.
  const now = Date.now();
  if (cached && now - cached.at < CACHE_TTL_MS) return cached.url;

  const url = await fetchFromWorker();
  cached = { url, at: now };
  return url;
}

/** Clear the session cache. Test-only helper. */
export function __resetBridgeCache(): void {
  cached = null;
}
